<template>
  <section>
    <PageTitle>
      <span v-html="$store.getters['app/getMenuName']('category')" />
    </PageTitle>

    <SwipeTab
      v-model="activeCategoryTabId"
      @change="onCategoryTabChange"
      :items="categoryTabItems"
      :background-color="true"
      :loading="loading"
      :sticky="true"
      ref="swipeTab"
    />

    <PageContent :loading="loading" :bottom-sticked="true">
      <Container>
        <div class="page-static" v-html="injectedHTML" ref="content" />
      </Container>
      <div class="tab has-cards" v-if="cards.count > 0">
        <div class="tab-list-wrapper">
          <div class="tab-list">
            <div
              class="tab-list-item"
              :class="[currentItemTab === 1 ? 'is-active' : '']"
              @click="onChangeItemTab(1)"
            >
              <i class="tab-list-icon fas fa-list"></i>一覧
            </div>
            <div
              class="tab-list-item is-favorite"
              :class="[currentItemTab === 2 ? 'is-active' : '']"
              @click="onChangeItemTab(2)"
            >
              <i class="tab-list-icon fas fa-heart"></i>お気に入り
              <p class="tab-list-badge">
                <span class="badge is-info">{{ bookmarks.count }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="tab-item-wrapper">
          <div
            class="tab-item"
            :class="[currentItemTab === 1 ? 'is-active' : '']"
          >
            <div
              class="tab-item-header"
              v-if="currentCategory.locked && hasBookmark"
            >
              <NoticeBox type="info" :icon-class="['fas', 'fa-lock']">
                現在、お気に入り機能が管理者によりロックされています
              </NoticeBox>
            </div>
            <div class="tab-item-body">
              <ul class="card-list">
                <li
                  class="card-list-item"
                  v-for="card in cards.item_list"
                  :key="card.item_id"
                >
                  <Card
                    :item="card"
                    :isLocked="currentCategory.locked || !hasBookmark"
                    @bookmark="onBookmark"
                  />
                </li>
              </ul>
              <infinite-loading @infinite="handleLoadItem">
                <template #spinner>
                  <div class="card-spinner" />
                </template>
              </infinite-loading>
            </div>
          </div>
          <div
            class="tab-item is-favorite"
            :class="[currentItemTab === 2 ? 'is-active' : '']"
          >
            <div class="tab-item-body">
              <div v-if="currentCategory.locked && hasBookmark">
                <NoticeBox type="info" :icon-class="['fas', 'fa-lock']">
                  現在、お気に入り機能が管理者によりロックされています
                </NoticeBox>
              </div>
              <div
                class="space-t-3 space-r-3 space-b-5 space-l-3"
                v-if="bookmarks.count === 0"
              >
                <NoticeBox
                  type="error"
                  :borderless="true"
                  class="space-b-2 text-bold"
                >
                  お気に入りアイテムが未設定です
                </NoticeBox>
                <p class="text-paragraph text-xs">
                  アイテム一覧の、「お気に入り登録」ボタンから登録することができます。<br>
                  ※お気に入りアイテムを登録しても、ご購入決定とはなりません。
                </p>
              </div>
              <ul class="card-list" v-else>
                <li
                  class="card-list-item"
                  v-for="bookmark in bookmarks.item_list"
                  :key="`favorite-${bookmark.item_id}`"
                >
                  <Card
                    :item="bookmark"
                    :isLocked="currentCategory.locked || !hasBookmark"
                    @bookmark="onBookmark"
                  />
                </li>
              </ul>
              <infinite-loading @infinite="handleLoadBookmarkItem">
                <template #spinner>
                  <div class="card-spinner" />
                </template>
              </infinite-loading>
            </div>
          </div>
        </div>
      </div>
    </PageContent>

    <Modal
      id="category-lock-modal"
      class="is-scrollable"
      title="ロック設定/解除の完了"
      icon="check"
      @ok="$modal.close('category-lock-modal')"
    >
      <template #default>
        <p>以下の内容が適用されました。</p>
        <h2 class="headline space-t-2 space-b-2">設定</h2>
        <p v-for="(name, i) in lockedNames" :key="`locked-${i}`" v-html="name"></p>
        <p v-if="lockedNames.length === 0">-</p>
        <h2 class="headline space-t-2 space-b-2">解除</h2>
        <p v-for="(name, i) in unlockedNames" :key="`unlocked-${i}`" v-html="name"></p>
        <p v-if="unlockedNames.length === 0">-</p>
      </template>
    </Modal>
  </section>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import Modal from '@/components/Modal'
import NoticeBox from '@/components/NoticeBox.vue'
import Card from '@/components/Card.vue'
import SwipeTab from '@/components/SwipeTab.vue'
import categoryApi from '@/api/category'
import { renderError } from '@/utils/error'
import { sanitizeHTML } from '@/utils/dom'
import initInjectedHTML from '@/utils/init-injected-html'

function getStickyTopHeight() {
  const getHeight = selector => {
    const elem = document.querySelector(selector)
    return elem ? elem.offsetHeight : 0
  }

  const titleHeight = getHeight('.page-title')
  const contentHeight = getHeight('.page-content')

  return titleHeight + contentHeight
}

export default {
  components: {
    InfiniteLoading,
    NoticeBox,
    Card,
    SwipeTab,
    Modal,
  },

  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('category'),
      bodyAttrs: { class: 'category' }
    }
  },

  data() {
    return {
      page: { category_id: 0, page_text: '' },
      cards: { count: 0, item_list: [] },
      bookmarks: { count: 0, item_list: [] },
      activeCategoryTabId: 0,
      currentItemTab: 1,
      tabOffset: { 1: 0, 2: 0 },
      loading: false
    }
  },

  computed: {
    injectedHTML() {
      return sanitizeHTML(this.page.page_text)
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    categoryTabItems() {
      return this.categories.map(c => ({
        key: c.category_id,
        label: this.$domUtils.decode(c.category_name),
      }))
    },
    categories() {
      return this.$store.state.category.items
    },
    currentCategory() {
      const cat = this.categories.find(
        ({ category_id }) => category_id === this.page.category_id
      )

      return cat || { category_name: '', locked: false }
    },
    lastItemId() {
      return this.cards.count
        ? this.cards.item_list[this.cards.item_list.length - 1].item_id
        : null
    },
    lastBookmarkItemId() {
      return this.bookmarks.count
        ? this.bookmarks.item_list[this.bookmarks.item_list.length - 1].item_id
        : null
    },
    loadOffset() {
      switch (this.currentItemTab) {
        case 1:
          return this.lastItemId
        case 2:
          return this.lastBookmarkItemId
        default:
          return null
      }
    },
    isCompleted() {
      switch (this.currentItemTab) {
        case 1:
          return this.cards.count === this.cards.item_list.length
        case 2:
          return (
            this.bookmarks.count === 0 ||
            this.bookmarks.count === this.bookmarks.item_list.length
          )
        default:
          return false
      }
    },
    hasBookmark() {
      return (
        this.$store.getters['auth/isCustomer'] ||
        this.$store.getters['auth/isPlanner']
      )
    },
    lockedNames() {
      return this.categories
        .filter(cat => cat.locked)
        .map(cat => cat.category_name)
    },
    unlockedNames() {
      return this.categories
        .filter(cat => !cat.locked)
        .map(cat => cat.category_name)
    }
  },

  methods: {
    async fetchCategory() {
      try {
        return await categoryApi.get(this.businessId)
      } catch(err) {
        renderError(err)
      }
    },
    async initCategoryPage() {
      await this.fetchPage()
      await Promise.all([
        this.fetchItem(),
        this.fetchBookmarkItem(),
      ])

      this.loading = false
    },
    async fetchPage() {
      const categoryId = this.$route.params.id || 0

      try {
        this.page = await categoryApi.getContext(this.businessId, categoryId)
        this.activeCategoryTabId = this.page.category_id
      } catch(err) {
        renderError(err)
      }
    },
    async fetchItem() {
      try {
        this.cards = await categoryApi.getItem(
          this.businessId,
          this.activeCategoryTabId,
          this.lastItemId
        )
      } catch(err) {
        renderError(err)
      }
    },
    async fetchBookmarkItem() {
      try {
        this.bookmarks = await categoryApi.getBookmarkItem(
          this.businessId,
          this.activeCategoryTabId,
          this.lastBookmarkItemId
        )
      } catch(err) {
        renderError(err)
      }
    },
    // カテゴリタブクリック時のページ切り替え
    onCategoryTabChange(id) {
      // コンテンツ読み込み中はタブ切り替え無効
      if (this.loading) {
        return false
      }

      window.scrollTo(0, 0)
      this.loading = true

      this.$wcRouter.push({ name: 'category.index', params: { id } })
    },
    // 無限スクロール時のデータ取得
    handleLoadItem(state) {
      if (this.isCompleted) {
        state.complete()
      }

      const { getItem: loadFunc } = categoryApi
      this.loadMore(this.cards.item_list, loadFunc, state)
    },
    handleLoadBookmarkItem(state) {
      if (this.isCompleted) {
        state.complete()
      }

      const { getBookmarkItem: loadFunc } = categoryApi
      this.loadMore(this.bookmarks.item_list, loadFunc, state)
    },
    async loadMore(list, loadFunc, state) {
      try {
        const response = await loadFunc(
          this.businessId,
          this.activeCategoryTabId,
          this.loadOffset
        )

        if (response.item_list.length === 0) {
          state.complete()
          return false
        }

        list.push(...response.item_list)
        state.loaded()
      } catch(err) {
        renderError(err)
      }
    },
    // お気に入り処理
    async onBookmark(categoryItem) {
      const bookmarkResult = !categoryItem.bookmarked

      const data = {
        item_id: categoryItem.item_id,
        bookmarked: bookmarkResult,
        revision: categoryItem.revision
      }

      this.bookmarks.count += bookmarkResult ? 1 : -1

      try {
        const response = await categoryApi.updateBookmark(this.businessId, data)

        this.cards.item_list = this.cards.item_list.map(card => {
          if (card.item_id === categoryItem.item_id) {
            card.bookmarked = bookmarkResult
            card.revision = response.revision
          }
          return card
        })

        this.bookmarks.count = response.count

        if (bookmarkResult) {
          this.addBookmark(
            Object.assign({}, categoryItem, { revision: response.revision })
          )
        } else {
          this.removeBookmark(categoryItem.item_id)
        }
      } catch(err) {
        renderError(err)
      }
    },
    addBookmark(card) {
      this.bookmarks.item_list.push(card)
      this.bookmarks.item_list.sort((a, b) => {
        if( a.item_id < b.item_id ) return -1;
        if( a.item_id > b.item_id ) return 1;
        return 0;
      })
    },
    removeBookmark(selectedId) {
      this.bookmarks.item_list = this.bookmarks.item_list.filter(
        item => item.item_id !== selectedId
      )
    },
    // タブ切り替え
    onChangeItemTab(nextItemTab) {
      const aboveTabHeight = getStickyTopHeight()

      // 前のタブの位置を保存する
      this.tabOffset[this.currentItemTab] = window.pageYOffset

      // sticky判定する
      const getNextTabOffset = () => {
        // タブより上に表示しているコンテンツの高さとスクロール量を比較しスティッキー判定
        // 判定結果によりスクロール位置を確定
        if (this.tabOffset[nextItemTab] > aboveTabHeight) {
          return this.tabOffset[nextItemTab]
        }

        return this.tabOffset[this.currentItemTab] > aboveTabHeight
          ? aboveTabHeight
          : this.tabOffset[this.currentItemTab]
      }

      this.tabOffset[nextItemTab] = getNextTabOffset()
      this.currentItemTab = nextItemTab

      this.$nextTick(() => {
        window.scrollTo(0, this.tabOffset[nextItemTab])
      })
    }
  },

  created() {    
    this.$withLoading(async () => {
      const [categories] = await Promise.all([
        this.fetchCategory(),
        this.initCategoryPage()
      ])

      this.$store.commit('category/SET_ITEMS', categories)
    })
  },

  watch: {
    $route() {
      this.cards = { count: 0, item_list: [] }
      this.bookmarks = { count: 0, item_list: [] }

      // ブラウザバックでのカテゴリ切り替えかどうかを判定
      if (!this.loading) {
        this.loading = true
        this.initCategoryPage().then(() => this.$refs.swipeTab.initCarousel())
        return false
      }

      this.initCategoryPage()
    },
    injectedHTML() {
      this.$nextTick(() => {
        initInjectedHTML(this.$refs.content)
      })
    }
  }
}
</script>
